import debounce from 'lodash/debounce';
import noop from 'lodash/noop';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface UseCancellableSearchOptions<TResult> {
  /**
   * Runs the actual search. Receives the query and an AbortSignal wired to the
   * in-flight request — thread the signal into your query so a superseded
   * search is cancelled at the network layer (e.g. Apollo
   * `context: { fetchOptions: { signal } }`, or `fetch(url, { signal })`).
   */
  execute: (query: string, signal: AbortSignal) => Promise<TResult[]>;
  /** Minimum query length before a search fires. Default 2. */
  minChars?: number;
  /** Debounce delay in ms. Default 250. */
  delayMs?: number;
}

export interface UseCancellableSearchResult<TResult> {
  searchResults: TResult[];
  /** Debounced trigger — call with the current query on each input change. */
  debouncedSearch: ReturnType<typeof debounce>;
  /**
   * Clears results AND cancels/aborts any pending or in-flight search. Call on
   * clear, select, and any other reset so a late resolve can't repopulate
   * stale results. Shaped as a state setter so it drops into combobox props.
   */
  resetSearchResults: React.Dispatch<React.SetStateAction<TResult[]>>;
}

/**
 * Debounced, cancellable server search for typeahead / combobox inputs. Owns
 * the debounce, an AbortController per request, and a stale-response guard, so
 * an older, superseded, or post-reset response can never overwrite newer
 * results. Query-agnostic: the caller supplies `execute` wrapping its own query
 * (Apollo, fetch, anything that accepts an AbortSignal).
 *
 * Two windows must be closed on reset, hence both a debounce cancel and an
 * abort: a scheduled-but-unfired search has no request to abort yet, while a
 * fired one has no timer to cancel.
 */
export function useCancellableSearch<TResult>({
  execute,
  minChars = 2,
  delayMs = 250,
}: UseCancellableSearchOptions<TResult>): UseCancellableSearchResult<TResult> {
  const [searchResults, setSearchResults] = useState<TResult[]>([]);

  // The in-flight search. Aborted before each new search and on every reset /
  // unmount, cancelling the request and marking its controller `aborted` so a
  // late (or out-of-order) resolve can't repopulate results for a stale term.
  const inFlightSearchRef = useRef<AbortController | null>(null);

  // Hold the latest execute without destabilizing the debounce — rebuilding
  // debouncedSearch on every render would drop pending calls and thrash the
  // cleanup effect.
  const executeRef = useRef(execute);
  executeRef.current = execute;

  const debouncedSearch = useMemo(
    () =>
      debounce((query: string) => {
        // Supersede any in-flight search before starting the next one.
        inFlightSearchRef.current?.abort();
        setSearchResults([]);
        if (!query || query.length < minChars) return;
        const controller = new AbortController();
        inFlightSearchRef.current = controller;
        executeRef
          .current(query, controller.signal)
          .then((results) => {
            // The link/fetch chain may resolve an aborted request with cached
            // data, so gate on the controller rather than trusting a reject.
            if (controller.signal.aborted) return;
            setSearchResults(results);
          })
          // Aborted or network error — the cleared results already stand.
          .catch(noop);
      }, delayMs),
    [minChars, delayMs],
  );

  useEffect(
    () => () => {
      debouncedSearch.cancel();
      inFlightSearchRef.current?.abort();
    },
    [debouncedSearch],
  );

  const resetSearchResults = useCallback<
    React.Dispatch<React.SetStateAction<TResult[]>>
  >(
    (value) => {
      debouncedSearch.cancel();
      inFlightSearchRef.current?.abort();
      setSearchResults(value);
    },
    [debouncedSearch],
  );

  return { searchResults, debouncedSearch, resetSearchResults };
}
